import { computed, readonly, ref } from 'vue'
import { defineStore } from 'pinia'
import type {
  PdfAccess,
  RecordsInfo,
  UserFreeRecordEligibility
} from '@/pages/records/config/records.types'
import { PDF_ACCESS_TYPES } from '@/pages/records/config/records.constants'
import { useStoreFeatureFlag } from '@/composables/useFeatureFlag'
import { DEFAULT_FEATURE_FLAGS } from '@/constants/featureFlags'

export const useRecordsStore = defineStore('records', () => {
  //#region LAYOUT STATE
  const _recordsType = ref<'pdf' | 'printed'>('pdf')
  const _orderSummaryActive = ref<boolean>(false)
  const _shippingFormActive = ref<boolean>(false)
  const _purchaseSessionId = ref<string>('')

  //#endregion
  //#region RECORDS USER AND DATA STATE
  const _pdfAccess = ref<PdfAccess>(PDF_ACCESS_TYPES.NONE)
  const _recordsInfo = ref<RecordsInfo>({
    premiumMembershipDiscount: 0.9, // todo: find out what this is for
    signedOptionCost: 10.0,
    pdf24HourAccessCost: useStoreFeatureFlag(
      DEFAULT_FEATURE_FLAGS.web_free_tier_restriction
    )
      ? 95.0
      : 55.0, // these are the hardcoded defaults and should be overridden by API data, this is just in case API call fails and to define the shape of the data
    pricePerPage: 0.2,
    pricePerPageFree: 0.25,
    printedRecordBaseCostFree: useStoreFeatureFlag(
      DEFAULT_FEATURE_FLAGS.web_free_tier_restriction
    )
      ? 150.0
      : 120.0,
    printedRecordBaseCostStandard: useStoreFeatureFlag(
      DEFAULT_FEATURE_FLAGS.web_free_tier_restriction
    )
      ? 60.0
      : 40.0,
    printedRecordBaseCostPremium: 30.0,
    printedRecordBaseCostBasic: useStoreFeatureFlag(
      DEFAULT_FEATURE_FLAGS.web_free_tier_restriction
    )
      ? 70.0
      : 60.0,
    emailOfUser: '',
    salesTaxFL: 0
  })
  const _freeRecordEligibility = ref<UserFreeRecordEligibility>({
    has24HourFreePdfRecordsAccess: false,
    isFreePdfRecordEligible: false,
    isFreePrintedRecordEligible: false,
    hasUnlimitedFreePdfRecords: false,
    lastFreePdfRecordDate: null,
    lastFreePrintedRecordDate: null,
    nextFreePdfRecordAvailableDate: null,
    nextFreePrintedRecordAvailableDate: null,
    pdfAccessExpiration: null
  })
  //#endregion

  //#region SETTERS
  function setRecordsType(value: 'pdf' | 'printed') {
    _recordsType.value = value
  }

  function setOrderSummaryActive(value: boolean) {
    _orderSummaryActive.value = value
  }

  function setShippingFormActive(value: boolean) {
    _shippingFormActive.value = value
  }

  function setPurchaseSessionId(value: string) {
    _purchaseSessionId.value = value
  }

  function setPdfAccess(value: PdfAccess) {
    _pdfAccess.value = value
  }

  function setRecordsInfo(value: RecordsInfo) {
    _recordsInfo.value = value
  }

  function setFreeRecordEligibility(value: UserFreeRecordEligibility) {
    _freeRecordEligibility.value = value
  }
  //#endregion

  const _isPrintedRecords = computed<boolean>(
    () => _recordsType.value === 'printed'
  )
  return {
    recordsType: readonly(_recordsType),
    orderSummaryActive: readonly(_orderSummaryActive),
    shippingFormActive: readonly(_shippingFormActive),
    purchaseSessionId: readonly(_purchaseSessionId),
    pdfAccess: readonly(_pdfAccess),
    recordsInfo: readonly(_recordsInfo),
    freeRecordEligibility: readonly(_freeRecordEligibility),
    setRecordsType,
    setOrderSummaryActive,
    setShippingFormActive,
    setPurchaseSessionId,
    setPdfAccess,
    setRecordsInfo,
    setFreeRecordEligibility,
    isPrintedRecords: readonly(_isPrintedRecords)
  }
})
